import { Box, Button } from '@mui/material';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Localized, PoolFormattedMessage, PoolTypography } from '@synergy/components';
import { PaloColumn, PaloListView, PaloRowAction, PaloThumb } from '@synergy/components-palo';
import { ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import * as HeaderBannerActions from '../store/header-banner-actions';
import * as HeaderBannerListActions from '../header-banner-list/store/header-banner-list-actions';
import * as HeaderBannerListSelectors from '../header-banner-list/store/header-banner-list-selectors';
import { HeaderBannerDeleteModal } from '../components/header-banner-delete-modal';
import { HeaderBannerDto } from '../models/header-banner-dto';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { PaloFilterPanel } from '../../../../common/components/palo/palo-filter-panel';
import { usePaloColumnFilters } from '../../../../common/components/palo/use-palo-column-filters';
import { usePaloListViews } from '../../../../common/components/palo/use-palo-list-views';
import { usePaloSort } from '../../../../common/components/palo/use-palo-sort';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const columns: PaloColumn<HeaderBannerDto>[] = [
    {
        key: 'picture',
        type: 'icon',
        head: <FormattedMessage id="app.table.column.picture" />,
        cell: banner => <PaloThumb src={banner.previewImage} width={128} height={26} />,
    },
    {
        key: 'title',
        type: 'name',
        sortKey: 'title',
        head: <FormattedMessage id="app.table.column.title" />,
        cell: banner => (
            <Localized value={banner.headline}>
                {({ localization }) =>
                    localization || (
                        <Box component="span" sx={{ color: 'text.disabled' }}>
                            <FormattedMessage id="app.table.column.noTitle" />
                        </Box>
                    )
                }
            </Localized>
        ),
    },
    {
        key: 'validFrom',
        type: 'date',
        sortKey: 'validFrom',
        head: <FormattedMessage id="app.table.column.validFrom" />,
        cell: banner => <FormattedDate value={banner.validFrom} year="numeric" month="short" day="2-digit" />,
    },
    {
        key: 'validTo',
        type: 'date',
        head: <FormattedMessage id="app.table.column.validTo" />,
        cell: banner => <FormattedDate value={banner.validTo} year="numeric" month="short" day="2-digit" />,
    },
];

export const PaloHeaderBannerList = (): ReactElement => {
    const { hiddenColumnKeys, shownColumnKeys, toggleColumn, moveColumn, orderColumns, savedFiltersMenu } =
        usePaloListViews('header-banners');
    const columnFilter = usePaloColumnFilters('header-banners');
    const intl = useIntl();
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { sort, params } = usePaloSort('validFrom', 'desc');
    const requesting = useAppSelector(HeaderBannerListSelectors.headerBannerListRequestingSelector);
    const paginated = useAppSelector(HeaderBannerListSelectors.headerBannerListItemsSelector);
    const [toDelete, setToDelete] = useState<number | null>(null);

    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);

    useEffect(() => {
        dispatch(HeaderBannerListActions.fetchHeaderBannerList(params));
    }, [dispatch, params]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const openDetail = (banner: HeaderBannerDto): void =>
        navigate(ROUTES.contentManagement.headerBanner.detail.replace(':id', banner.id.toString()));

    const handleDelete = (): void => {
        if (toDelete) {
            dispatch(HeaderBannerActions.deleteHeaderBanner(toDelete));
        }
        setToDelete(null);
    };

    const rowActions = (banner: HeaderBannerDto): PaloRowAction[] => [
        { label: 'Öffnen', onClick: () => openDetail(banner) },
        { label: 'Löschen', onClick: () => setToDelete(banner.id) },
    ];

    const headerActions = (
        <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`${ROUTES.contentManagement.headerBanner.list}/new`)}
        >
            <FormattedMessage id="app.contentManagement.newHeaderBanner" />
        </Button>
    );

    return (
        <PaloListView<HeaderBannerDto>
            // The screen heading carries the "max. 3 active" hint; the tab takes the name only.
            pageTitle={intl.formatMessage({ id: 'app.contentManagement.headerBanner' }, { value: '' }).trim()}
            sort={sort}
            onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
            title={
                <PoolFormattedMessage
                    id="app.contentManagement.headerBanner"
                    values={{
                        value: (
                            <PoolTypography
                                component="span"
                                typography="body1"
                                id="app.contentManagement.maxHeaderBanners"
                            />
                        ),
                    }}
                />
            }
            headerActions={headerActions}
            filters={
                <>
                    <PaloFilterPanel listKey="header-banners" />
                    {savedFiltersMenu}
                </>
            }
            columns={orderColumns(columns)}
            columnFilter={columnFilter}
            onMoveColumn={moveColumn}
            hiddenColumnKeys={hiddenColumnKeys}
            shownColumnKeys={shownColumnKeys}
            onToggleColumn={toggleColumn}
            items={paginated.items}
            getRowId={banner => banner.id}
            requesting={requesting}
            emptyLabel="Keine Header-Banner."
            onRowClick={openDetail}
            rowActions={rowActions}
            pagination={{
                count: paginated.total,
                page,
                limit,
                onPageChange: newPage => updateParams({ page: String(newPage) }),
                onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
            }}
        >
            <HeaderBannerDeleteModal
                isVisible={!!toDelete}
                onDelete={handleDelete}
                handleClose={() => setToDelete(null)}
                id={toDelete}
            />
        </PaloListView>
    );
};
